import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { GenerationSaleSubKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { TradingPartnerListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldValues,
  type UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { DsSelect } from '~/components/common/DsSelect'
import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import {
  PLAN_CATEGORY_OPTIONS,
  planCategoryLabel,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/planCategory'
import type { GenerationSalesMasterFormValues } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/schema'
import type { PlantMasterListItemRow } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useListPlantMasters'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { PlanMasterBgsSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/PlanMasterBgsSection'
import { PlanMasterCodesSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/PlanMasterCodesSection'
import { PlanMasterPlantsSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/PlanMasterPlantsSection'

type BaseProps = {
  control: Control<GenerationSalesMasterFormValues>
  errors: FieldErrors<GenerationSalesMasterFormValues>
  /** list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。 */
  allBgMasters: BgMasterRow[] | undefined
  bgMasters: BgMasterRow[]
  plantMasters: PlantMasterListItemRow[]
  /** list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。 */
  submitters: SubmitterMasterRow[] | undefined
  tradingPartners: TradingPartnerListItem[]
  codesErrorMessage: string | undefined
  submitterAreaCode: AreaCode | undefined
  selfSubmitterCode: string | undefined
  bgAddDisabledReason:
    | 'keyMissing'
    | 'noSubmitterBgs'
    | 'noPlants'
    | 'hasBoth'
    | undefined
}

type Props =
  | (BaseProps & {
      isEdit: false
      submitterList: SubmitterMasterRow[]
      isLoadingFormOptions: boolean
      setValue: UseFormSetValue<GenerationSalesMasterFormValues>
      planCategory: GenerationSaleSubKind
    })
  | (BaseProps & {
      isEdit: true
      planCategory: GenerationSaleSubKind | undefined
    })

/**
 * 発電販売計画マスタの Create / Edit 共通 form fields。
 *
 * Create (isEdit=false): submitterMasterId select で選択、planCategory は
 * props 経由の一覧フィルタ選択値を disabled で表示。 Edit (isEdit=true):
 * submitterCode を readonly Input で表示、planCategory も fetched から
 * disabled で表示。 他 fields (note / TopLevelValidPeriodFields / PlanMaster*Section)
 * は共通。
 */
export const GenerationSalesPlanMasterFormFields = (
  props: Props,
): React.ReactElement => {
  const { t } = useTranslation()
  const {
    control,
    errors,
    allBgMasters,
    bgMasters,
    plantMasters,
    submitters,
    tradingPartners,
    codesErrorMessage,
    submitterAreaCode,
    selfSubmitterCode,
    bgAddDisabledReason,
    isEdit,
  } = props
  const idPrefix = isEdit ? 'genPlanMaster-edit' : 'genPlanMaster'

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix={idPrefix}
        />
      )}

      {isEdit ? (
        <Controller
          control={control}
          name="submitterCode"
          render={({ field }) => (
            <FieldRoot>
              <FieldLabel htmlFor={`${idPrefix}-submitterCode`}>
                {t(
                  'page:adminOcctoMaster.generationSalesPlanMaster.form.submitterCode',
                )}
              </FieldLabel>
              <ImmutableInput
                {...field}
                id={`${idPrefix}-submitterCode`}
                readOnly
              />
            </FieldRoot>
          )}
        />
      ) : (
        /* submitterMasterId — 計画提出者ドロップダウン。 単一 select の正は
           DS `Select` (`~/components/common/FormSelect` の JSDoc 参照)。
           native の `<option value="">…未選択</option>` は placeholder へ移す
           (option として残すと選択肢に並ぶため)。 */
        <FieldRoot
          alignItems="stretch"
          invalid={errors.submitterMasterId !== undefined}
          required
        >
          <FieldLabel>
            {t(
              'page:adminOcctoMaster.generationSalesPlanMaster.form.submitterMaster',
            )}
            <FieldRequiredIndicator />
          </FieldLabel>
          {props.isLoadingFormOptions ? (
            <Spinner />
          ) : (
            <FormSelect
              aria-label={t(
                'page:adminOcctoMaster.generationSalesPlanMaster.form.submitterMaster',
              )}
              control={control}
              name="submitterMasterId"
              onValueChange={(selectedId) => {
                // 選択した提出者の submitterCode を自動設定する
                const found = props.submitterList.find(
                  (s) => s.id === selectedId,
                )
                props.setValue('submitterCode', found?.submitterCode ?? '')
              }}
              options={props.submitterList.map((s) => ({
                value: s.id,
                label: `${s.submitterCode}: ${s.submitterName}`,
              }))}
              placeholder={t(
                'page:adminOcctoMaster.generationSalesPlanMaster.form.submitterMasterUnselected',
              )}
            />
          )}
          {errors.submitterMasterId !== undefined && (
            <FieldErrorText>{errors.submitterMasterId.message}</FieldErrorText>
          )}
        </FieldRoot>
      )}

      {/* planCategory — disabled 表示 (AC-05/AC-06)。 UK の一部で immutable
            のため編集不可 (Update request にも含めない)。 form 値ではなく
            props の表示だけ (Create=一覧フィルタ選択値 / Edit=取得結果) なので
            RHF ではなく `DsSelect` を使い、 `isDisabled` で操作不可にする。 */}
      <FieldRoot disabled>
        <FieldLabel>
          {t(
            'page:adminOcctoMaster.generationSalesPlanMaster.column.planCategory',
          )}
        </FieldLabel>
        <DsSelect
          aria-label={t(
            'page:adminOcctoMaster.generationSalesPlanMaster.column.planCategory',
          )}
          isDisabled
          onChange={() => {
            // 表示専用 (immutable)。 isDisabled のため呼ばれない。
          }}
          options={PLAN_CATEGORY_OPTIONS.map((option) => ({
            value: String(option),
            label: planCategoryLabel(option),
          }))}
          value={props.planCategory}
        />
      </FieldRoot>

      {/* note (任意)。 上限は schema / proto CEL の max_len 100 に揃える
          (design HTML #pm-note は 200 だが実 contract は 100)。 */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel htmlFor={`${idPrefix}-note`}>
              {t('page:adminOcctoMaster.generationSalesPlanMaster.form.note')}
            </FieldLabel>
            <Textarea
              {...field}
              id={`${idPrefix}-note`}
              maxLength={100}
              value={field.value ?? ''}
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
            <Text
              as="span"
              textStyle="captionNormal"
              marginTop={1}
              color="Text.midEmphasis"
            >
              {t(
                'page:adminOcctoMaster.generationSalesPlanMaster.form.noteHint',
              )}
            </Text>
          </FieldRoot>
        )}
      />

      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix={idPrefix}
        validFromError={errors.validFrom?.message}
        validToError={errors.validTo?.message}
      />

      {/* Codes 4-button 編集 UI (D4, RG6): 全件 BG/submitter 使用 (BG7 スコープ外) */}
      <PlanMasterCodesSection
        bgMasters={allBgMasters}
        codesErrorMessage={codesErrorMessage}
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.generationSalesPlanMaster"
        selfAreaCode={submitterAreaCode}
        selfKey={
          selfSubmitterCode !== '' && selfSubmitterCode !== undefined
            ? selfSubmitterCode
            : undefined
        }
        selfKeyMissingI18nKey="adminOcctoMaster.generationSalesPlanMaster.form.submitterCodeRequired"
        submitters={submitters}
        tradingPartners={tradingPartners}
      />

      {/* 所属BG 編集 UI (RG2, RG6): 発電区分フィルタ済み bgs + area narrow +
            有効期間 narrow (AC-01)。 AC-06: 所属BG未登録/配下発電所未登録の
            inline note + 削除済み判定 rework (allBgMasters 経由)。 */}
      <PlanMasterBgsSection
        allBgMasters={allBgMasters}
        bgAddDisabledReason={bgAddDisabledReason}
        bgMasters={bgMasters}
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.generationSalesPlanMaster"
        selfAreaCode={submitterAreaCode}
      />

      {/* 所属発電所 編集 UI (gen-sales-plant-master-select 設計書):
            区分フィルタなし全件 plants + area narrow (AC-03) + 既選択除外 (AC-04) */}
      <PlanMasterPlantsSection
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.generationSalesPlanMaster"
        plantMasters={plantMasters}
        selfAreaCode={submitterAreaCode}
      />
    </Stack>
  )
}
